/**
 * Cloudflare R2 Storage Service
 *
 * Tento servis poskytuje prístup k Cloudflare R2 bucketu cez Worker proxy.
 *
 * Poznámka:
 * - Pre lokálny vývoj môže byť R2 vypnuté (bez VITE_R2_WORKER_URL). Vtedy upload zlyhá
 *   a aplikácia môže používať fallback (napr. localStorage + blob URLs).
 */

// Espacero-only: no legacy domabazar worker defaults.
// Set this in your env (.env.local / Cloudflare Pages env vars):
//   VITE_R2_WORKER_URL=https://espacero-r2-upload.<account>.workers.dev
//
// NOTE: some environments accidentally include a route suffix (e.g. ".../upload").
// We normalize to the Worker base URL to avoid generating "/upload/upload" etc.

import { getWorkerUrl } from '../utils/env';
import { resizeImage, isImage } from '../utils/imageProcessor';

const REQUEST_TIMEOUT_MS = 60000;

function normalizeWorkerBaseUrl(raw: string): string {
  try {
    const s = String(raw || '').trim();
    if (!s) return '';
    // Remove common route suffixes if someone pasted an endpoint URL.
    return s.replace(/\/(upload|delete|health|file|url)\/?$/i, '').replace(/\/+$/, '');
  } catch {
    return '';
  }
}

export function getR2WorkerBaseUrl(): string {
  return normalizeWorkerBaseUrl(getWorkerUrl('R2'));
}

export function getPublicR2FileUrl(filePathOrKey: string): string {
  const base = getR2WorkerBaseUrl();
  if (!base) return '';
  const p = String(filePathOrKey || '').replace(/^\/+/, '');
  if (!p) return '';
  // If caller already passed "file/..." or "/file/...", keep only the key part.
  const key = p.startsWith('file/') ? p.slice('file/'.length) : p;
  return `${base}/file/${key}`;
}

export function isR2Configured(): boolean {
  return !!getR2WorkerBaseUrl();
}

async function fetchWithTimeout(url: string, opts: RequestInit, timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(url, { ...opts, signal: controller.signal });
  } catch (err: any) {
    if (err?.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeoutMs} ms`);
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}

export interface UploadResult {
  url: string;
  path: string;
  error: Error | null;
}

/**
 * Pomocná funkcia na získanie autentifikačných hlavičiek (Bearer token)
 * Mirroruje logiku v services/d1.ts pre konzistenciu.
 */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const headers: Record<string, string> = {};

  // 1. Clerk token (priorita)
  if (typeof window !== 'undefined' && (window as any).Clerk?.session) {
    try {
      const token = await (window as any).Clerk.session.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        return headers;
      }
    } catch (e) {
      console.error('R2 Service: Failed to get Clerk token:', e);
    }
  }

  return headers;
}

const MIME_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  heic: 'image/heic',
  heif: 'image/heif',
  svg: 'image/svg+xml',
};

/** Extension the R2 object should carry, derived from what we actually send. */
function extensionForType(type: string, fallbackName: string): string {
  const byType: Record<string, string> = {
    'image/webp': 'webp',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/svg+xml': 'svg',
    'image/heic': 'heic',
    'image/heif': 'heif',
  };
  return byType[type] || fallbackName.split('.').pop()?.toLowerCase() || 'bin';
}

/**
 * Some files reach us without a MIME type (iPhone exports, network drives,
 * renamed files). Canvas and the Worker both key off the type, so recover it
 * from the extension before anything looks at it.
 */
function withKnownType(file: File): File {
  if (file.type) return file;
  const type = MIME_BY_EXTENSION[file.name.split('.').pop()?.toLowerCase() || ''];
  if (!type) return file;
  return new File([file], file.name, { type, lastModified: file.lastModified });
}

/** Canvas resize to WebP. Resolves with the original file if it cannot decode. */
function canvasResizeToWebp(file: File, maxDim: number): Promise<Blob> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onerror = () => resolve(file);
    reader.onload = (e) => {
      const img = new Image();
      // Without these the promise never settles for a format the browser
      // cannot decode (HEIC), and the upload spinner runs forever.
      img.onerror = () => resolve(file);
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxDim) {
            height *= maxDim / width;
            width = maxDim;
          }
        } else {
          if (height > maxDim) {
            width *= maxDim / height;
            height = maxDim;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => {
          resolve(blob || file);
        }, 'image/webp', 0.8);
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Resize an image before upload to save bandwidth and storage.
 * HEIC/HEIF (iPhone) goes through the shared processor first — canvas cannot
 * decode it, so it would otherwise be uploaded raw or hang the upload.
 */
async function prepareForUpload(file: File, maxDim = 1600): Promise<Blob> {
  const ext = file.name.split('.').pop()?.toLowerCase();
  const isHeic = /heic|heif/i.test(file.type) || ext === 'heic' || ext === 'heif';

  if (isHeic) {
    try {
      return await resizeImage(file, { maxDimension: maxDim, quality: 0.8, mimeType: 'image/webp' });
    } catch (e) {
      console.warn('R2: HEIC conversion failed, uploading the original file', e);
      return file;
    }
  }

  return canvasResizeToWebp(file, maxDim);
}

/**
 * Nahrať obrázok do R2 bucketu
 */
export const uploadImage = async (
  file: File,
  userId: string,
  listingId?: string,
  token?: string
): Promise<UploadResult> => {
  try {
    const workerUrl = getR2WorkerBaseUrl();
    if (!workerUrl) {
      throw new Error('VITE_R2_WORKER_URL nie je nastavený');
    }

    // 1. Process the file, then name it after what we actually send.
    // isImage() also accepts files the browser gave no MIME type to (iPhone
    // photos, network drives) — those used to be uploaded raw.
    const sourceFile = withKnownType(file);
    const processedFile = isImage(sourceFile) ? await prepareForUpload(sourceFile) : sourceFile;
    const fileExt = extensionForType(processedFile.type, sourceFile.name);

    // Vytvoriť jedinečný názov súboru
    const fileName = `${userId}/${listingId || 'temp'}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const path = `listing-images/${fileName}`;

    // Vytvoriť FormData
    const formData = new FormData();
    formData.append('file', processedFile, `media.${fileExt}`);
    formData.append('path', path);

    // Build auth headers - use provided token or fetch from storage
    const headers = token ? { 'Authorization': `Bearer ${token}` } : await getAuthHeaders();


    // Nahrať súbor cez Worker
    const response = await fetchWithTimeout(`${workerUrl}/upload`, {
      method: 'POST',
      headers,
      body: formData,
      credentials: 'include' // CRITICAL: Required for same-site cookies (espacero_session)
    }, REQUEST_TIMEOUT_MS);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
      let msg = errorData.error || `Nahratie zlyhalo: ${response.status}`;
      if (errorData.errorDetails) msg += ` (${errorData.errorDetails})`;
      throw new Error(msg);
    }

    const data = await response.json();

    return {
      url: data.url,
      path: fileName,
      error: null
    };
  } catch (error: any) {
    console.error('Chyba pri nahrávaní obrázka do R2:', error);

    let errorMessage = 'Nepodarilo sa nahrať obrázok.';
    if (error?.message?.includes('CORS')) {
      errorMessage = 'CORS chyba: Skontrolujte R2 Worker CORS nastavenia.';
    } else if (error?.message) {
      errorMessage = error.message;
    }

    return {
      url: '',
      path: '',
      error: new Error(errorMessage)
    };
  }
};

/**
 * Nahrať viacero obrázkov naraz
 */
export const uploadImages = async (
  files: File[],
  userId: string,
  listingId?: string
): Promise<UploadResult[]> => {
  const uploadPromises = files.map(file => uploadImage(file, userId, listingId));
  return Promise.all(uploadPromises);
};

/**
 * Vymazať obrázok z R2 bucketu
 */
export const deleteImage = async (path: string): Promise<{ error: Error | null }> => {
  try {
    const workerUrl = getR2WorkerBaseUrl();
    if (!workerUrl) {
      throw new Error('VITE_R2_WORKER_URL nie je nastavený');
    }
    const fullPath = `listing-images/${path}`;

    // Build auth headers
    const authHeaders = await getAuthHeaders();
    const headers: Record<string, string> = {
      ...authHeaders,
      'Content-Type': 'application/json',
    };

    const response = await fetchWithTimeout(`${workerUrl}/delete`, {
      method: 'DELETE',
      headers,
      body: JSON.stringify({ path: fullPath }),
      credentials: 'include' // CRITICAL: Required for same-site cookies (espacero_session)
    }, REQUEST_TIMEOUT_MS);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
      throw new Error(errorData.error || `Delete failed: ${response.status}`);
    }

    return { error: null };
  } catch (error) {
    console.error('Chyba pri mazaní obrázka z R2:', error);
    return { error: error as Error };
  }
};

/**
 * Vymazať viacero obrázkov naraz
 */
export const deleteImages = async (paths: string[]): Promise<{ error: Error | null }> => {
  try {
    const deletePromises = paths.map(path => deleteImage(path));
    await Promise.all(deletePromises);
    return { error: null };
  } catch (error) {
    console.error('Chyba pri mazaní obrázkov z R2:', error);
    return { error: error as Error };
  }
};

/**
 * Skontrolovať, či bucket existuje
 */
export const checkBucketExists = async (): Promise<boolean> => {
  try {
    const workerUrl = getR2WorkerBaseUrl();
    if (!workerUrl) {
      return false;
    }
    const response = await fetch(`${workerUrl}/health`);
    if (response.ok) {
      const data = await response.json();
      return data.hasBucket === true;
    }
    return false;
  } catch {
    return false;
  }
};


